import { isAxiosError } from "axios";
import { isEqual } from "lodash";

import { isRequestTimeoutError } from "utils";

import { COVER_CROP_MIX_TYPE, INPUT_COST_PRESET_PCT, OUTPUT_COST_PRESET_PCT } from "consts";

import type { ProjectSettings, ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { presetFromPct } from "pages/app/scenario-analysis/utils/forecastPresets";
import { isDeviceOfflineError } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

export type ForecastTrigger = "manual" | "settings_change" | "project_settings" | "tab_arrival" | "language_change" | "project_load" | "field_reselect";

export type ForecastErrorType = "offline" | "timeout" | "unavailable" | "api_error";

export type ForecastFailReason = ForecastErrorType | "incomplete_rotation" | "field_reselect" | "missing_country" | "save_failed";

export type AutosavedChangeType = "rotation" | "economics" | "rename";

export const getCoverCropMixKey = (coverCropId: string | null | undefined): string | undefined =>
  Object.entries(COVER_CROP_MIX_TYPE)
    .find(([, id]) => id === coverCropId)?.[0]
    .toLowerCase();

export const classifyForecastError = (error: unknown): ForecastErrorType => {
  if (isDeviceOfflineError(error)) return "offline";
  if (isRequestTimeoutError(error)) return "timeout";
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 502 || status === 503) return "unavailable";
  return "api_error";
};

type SettingsBarChange = { setting: "cost_preset" | "market_preset" | "timeline"; value: string | number | null };

export const getSettingsBarChange = (previous: ScenarioSettings, next: ScenarioSettings): SettingsBarChange | null => {
  if (next.costs !== previous.costs) return { setting: "cost_preset", value: presetFromPct(next.costs, INPUT_COST_PRESET_PCT) };
  if (next.market !== previous.market) return { setting: "market_preset", value: presetFromPct(next.market, OUTPUT_COST_PRESET_PCT) };
  if (next.timeline !== previous.timeline) return { setting: "timeline", value: Number(next.timeline) };
  return null;
};

/** We send key names only. The values hold field ids and costs. */
export const getChangedProjectSettingKeys = (previous: ProjectSettings, updated: ProjectSettings): string[] => {
  const changes: [string, boolean][] = [
    ["market_price_trend", previous.forecastControls.marketPriceTrend !== updated.forecastControls.marketPriceTrend],
    ["cost_development", previous.forecastControls.costDevelopment !== updated.forecastControls.costDevelopment],
    ["location", !isEqual(previous.location, updated.location)],
    ["variable_costs", !isEqual(previous.variableCosts, updated.variableCosts)],
    ["model_settings", !isEqual(previous.modelSettings, updated.modelSettings)],
  ];
  return changes.filter(([, changed]) => changed).map(([key]) => key);
};
